import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Tooltip from '@material-ui/core/Tooltip';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import IconButton from '@material-ui/core/IconButton';
import DoneIcon from '@material-ui/icons/Done';
import WaitingIcon from '@material-ui/icons/FileCopy';
import FailedIcon from '@material-ui/icons/Warning';
import { FormatError } from 'grb-webui/services/error';
import {
  UPLOADER_UPLOAD_STATUS,
  UPLOADER_ERROR_STATUS,
  UPLOADER_ABORTED_STATUS,
  UPLOADER_DONE_STATUS,
} from '../../services/BatchService';
import FileSizeFormatter from '../../services/FileSizeFormatter';
import PercentProgress from './PercentProgress';
import './FileListItem.scss';

const DoneComponent = () => (
  <div className="grb-fileitem-icon grb-fileitem-icondone">
    <DoneIcon fontSize="large" />
  </div>
);

const WaitingComponent = () => (
  <div className="grb-fileitem-icon grb-fileitem-iconwaiting">
    <WaitingIcon fontSize="large" />
  </div>
);

const AbortedComponent = ({ object }) => {
  return (
    <div className="grb-fileitem-icon grb-fileitem-iconaborted">
      <Tooltip title={FormatError(object.error)}>
        <FailedIcon fontSize="large" />
      </Tooltip>
    </div>
  );
};

AbortedComponent.propTypes = {
  object: PropTypes.shape({
    error: PropTypes.string,
  }).isRequired,
};

const FailedComponent = ({ object }) => (
  <div className="grb-fileitem-icon grb-fileitem-iconfailed">
    <Tooltip title={FormatError(object.error)}>
      <FailedIcon fontSize="large" />
    </Tooltip>
  </div>
);

FailedComponent.propTypes = {
  object: PropTypes.shape({
    error: PropTypes.string,
  }).isRequired,
};

const FileListItem = (props) => {
  const { node = {}, onDelete, uploader } = props;
  const { name, file } = node;
  const [percent, setPercent] = useState(node.percent);
  const [status, setStatus] = useState(node.status);

  useEffect(() => {
    const sub = {
      fileStatusChanged(changedNode) {
        if (changedNode === node) {
          if (node.status !== status) {
            setStatus(node.status);
          }
          if (node.percent !== percent) {
            setPercent(node.percent);
          }
        }
      },
    };

    uploader.subscribeUpload(sub);
    // Specify how to clean up after this effect:
    return function cleanup() {
      uploader.unsubscribeUpload(sub);
    };
  }, [node, uploader]);

  const { size } = file;
  const handleDelete = onDelete ? () => {
    onDelete(node);
  } : undefined;
  const object = {
    name,
    length: size,
    error: node.error,
  };
  const error = node.validationError;
  let StatusComponent;
  switch (status) {
    case UPLOADER_DONE_STATUS: StatusComponent = DoneComponent; break;
    case UPLOADER_UPLOAD_STATUS: StatusComponent = PercentProgress; break;
    case UPLOADER_ERROR_STATUS: StatusComponent = FailedComponent; break;
    case UPLOADER_ABORTED_STATUS: StatusComponent = AbortedComponent; break;
    default: {
      StatusComponent = error ? FailedComponent : WaitingComponent;
      break;
    }
  }

  const { storeId, id, length, container } = object;
  const fsize = length ? ` - ${FileSizeFormatter(length)}` : '';
  const errorMsg = error && FormatError(error);

  return (
    <ListItem
      classes={{ container: classnames('grb-batchobject-item', 'grb-fileupload-item', error && 'grb-error') }}
    >
      <ListItemAvatar>
        <StatusComponent object={object} />
      </ListItemAvatar>
      <ListItemText primary={`${name}${fsize}`} secondary={errorMsg || container || id || storeId} />
      <ListItemSecondaryAction>
        {handleDelete && (
          <IconButton aria-label="Delete" size="small" onClick={handleDelete}>
            <svg className="MuiSvgIcon-root" focusable="false" viewBox="0 0 24 24" aria-hidden="true" role="presentation">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              <path fill="none" d="M0 0h24v24H0z" />
            </svg>
          </IconButton>
        )}
      </ListItemSecondaryAction>
    </ListItem>
  );
};

FileListItem.propTypes = {
  node: PropTypes.shape({}).isRequired,
  uploader: PropTypes.shape({
    subscribeUpload: PropTypes.func.isRequired,
    unsubscribeUpload: PropTypes.func.isRequired,
  }).isRequired,
  onDelete: PropTypes.func,
};

FileListItem.defaultProps = {
  onDelete: undefined,
};

export default FileListItem;
